import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { render } from "react-dom";
import ReplayIcon from "../shared/svg/ReplayIcon";
import { useReactAppState } from "../shared/containers/utils";
import { getUserPlayVideoPermission, isMobileBrowser } from '../brightcove-video-playlist/utils';
import useAuth0Session from '../brightcove-video-playlist/hooks/useAuth0Session';
import RequiredAuthModal from '../brightcove-video-playlist/components/RequiredAuthModal';
import { trackUserFreemiumLoginWallCtaClicked } from '../../libraries/_modules/tracking/helpers';

export const BrightCoveSingleVideo = ({ options }) => {
  const { urlList, t } = useReactAppState();
  const { defaultAdUrl } = urlList || {};
  const isUserLogged = useAuth0Session();
  const [modalVisible, setModalVisible] = useState(false);

  const _child = useRef(null);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const { ref, inView } = useInView({
    threshold: .9,
  });

  const playerId = `mls-o-video-player__instance-${options?.accountId}-${options?.videoId}`;
  const containerId = `mls-c-${playerId}`;

  const { accountWall } = options || {};

  const addIcons = (brightCovePlayerInstance) => {
    const $player = brightCovePlayerInstance?.el();

    if ($player) {
      const endScreen = document.createElement('div');
      endScreen.className = `mls-o-video-player__end-screen vjs-hidden`;
      $player.append(endScreen);
    }
  };

  const handleGoBackClick = () => {
    trackUserFreemiumLoginWallCtaClicked({
      freemiumType: "video",
      ctaAction: "goback",
    });
    setModalVisible(false);
  }

  useEffect(() => {
    if (_child?.current?.player) {
      if (!inView && !_child.current.player.isFullscreen()) {
        _child.current.player.pause();
      }
    }
  }, [ref, inView, _child]);

  useEffect(() => {
    const endScreen = _child?.current?.querySelector('.mls-o-video-player__end-screen');
    if (endScreen) {
      if (isVideoEnded) {
        endScreen.className = `mls-o-video-player__end-screen`;
        render(
          <div className="mls-o-video-player__end-screen-details">
            <div className="mls-o-video-player__end-screen-button-group">
              <div className="mls-o-video-player__end-screen-replay">
                <ReplayIcon handleClick={() => _child.current.player.play()} size="large" />
                <div className="mls-o-video-player__end-screen-text">{t("replay")}</div>
              </div>
            </div>
          </div>,
          endScreen
        );
      }
      else {
        endScreen.className = `mls-o-video-player__end-screen vjs-hidden`;
        render(
          null,
          endScreen
        );
      }
    }
  }, [isVideoEnded]);

  useEffect(() => {
    if (isUserLogged !== undefined) {
      console.log("IS USER LOGGED", isUserLogged)
      const $script = document.createElement("script");
      $script.async = true;
      $script.src = `https://players.brightcove.net/${options?.accountId}/default_default/index.min.js`;
      $script.charset = "utf-8";
      $script.onload = () => {
        const bc = window.bc;
        const player = bc(`${playerId}`);
  
        player.ready(function () {
          addIcons(player);
          player.ima3.settings.serverUrl = options?.adUrl || defaultAdUrl || "";
  
          // confirm isUserLogged has loaded when mounted
  
          const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
          if (!canUserPlayVideo) {
            player.off();
            player.controlBar?.progressControl?.disable();
            player.controlBar?.fullscreenToggle?.disable();
            if (options.isAutoplay) {
              setModalVisible(true);
            }
          } else {
            const isMobile = isMobileBrowser();
            if (isMobile && options.isAutoplay) {
              player.play();
            }
          }
  
          player.on('play', handleVideoPlay);
          player.on('touchend', handleVideoPlay);
          player.on('ended', () => {
            setIsVideoEnded(true);
            if (!player.el().classList.contains('vjs-controls-disabled')) {
              player.el().classList.add('vjs-controls-disabled');
            }
          });
  
          function handleVideoPlay() {
            const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
  
            if (canUserPlayVideo) {
              setIsVideoEnded(false);
              if (player.el().classList.contains('vjs-controls-disabled')) {
                player.el().classList.remove('vjs-controls-disabled');
              }
            } else {
              setModalVisible(true);
              player.pause();
            }
          }
        });
      };
      document.getElementById(containerId)?.append($script);
    }
  }, [isUserLogged]);


  React.useEffect(() => {
    // check if userLogged doesn't affect re-render this logic
    const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
    if (canUserPlayVideo) {
      const lockIcon = document.querySelector('h2.fa-text__title .fa-text__icon');
      lockIcon?.remove();
    }

    
  }, [accountWall, isUserLogged])

  const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
  const isMobile = isMobileBrowser();

  let autoPlay = {};
  if (canUserPlayVideo && options?.isAutoplay && !isMobile) {
    autoPlay = { autoplay: true };
  }

  return (
    <div ref={ref} id={`mls-c-${playerId}`} className="mls-c-video-component__player d3-o-media-object">
      <RequiredAuthModal
        visible={modalVisible}
        onGoBackClick={handleGoBackClick}
        accountWall={accountWall}
      />
      <div className="mls-o-video-player">
        <video-js ref={_child}
          id={playerId}
          data-account={options?.accountId}
          data-video-id={options?.videoId}
          class="vjs-fluid"
          preload="auto"
          controls=""
          playsinline
          {...autoPlay}
        />
      </div>
    </div>
  );
};

export default BrightCoveSingleVideo;
